import React, { useMemo } from 'react';
import { Calendar, Card, Spin } from 'antd';
import moment from 'moment';
import { ToolOutlined, CarOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { formatDuration } from '../../utils/WrenchModeEventUtils';
import { WrenchHubUserAnalytic } from '../../thunks/wrench_mode_events_thunks';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';

interface WrenchModeLogsSidebarProps {
	selectedDate: moment.Moment;
	onDateChange: (date: moment.Moment) => void;
	techAnalytics: WrenchHubUserAnalytic[];
	loading: boolean;
}

const WrenchModeLogsSidebar: React.FC<WrenchModeLogsSidebarProps> = ({
	selectedDate,
	onDateChange,
	techAnalytics,
	loading,
}) => {
	const sortedAnalytics = useMemo(
		() => [...techAnalytics].sort((a, b) => (b.totalTimeMs || 0) - (a.totalTimeMs || 0)),
		[techAnalytics]
	);

	return (
		<div className="wrenchModeLogsSidebar flex w-1/5 flex-none flex-col gap-s overflow-y-auto border-r border-grey-4">
			<Calendar
				fullscreen={false}
				value={selectedDate}
				onSelect={onDateChange}
				headerRender={({ value }) => {
					return (
						<div className="flex items-center justify-between px-3 py-2">
							<LeftOutlined
								className="cursor-pointer text-character-secondary"
								onClick={() => onDateChange(value.clone().subtract(1, 'month').startOf('month'))}
							/>
							<span className="h5medium text-character-primary">{value.format('MMM YYYY')}</span>
							<RightOutlined
								className="cursor-pointer text-character-secondary"
								onClick={() => onDateChange(value.clone().add(1, 'month').startOf('month'))}
							/>
						</div>
					);
				}}
			/>
			<Card
				title={'Monthly Summary: ' + selectedDate.format('MMM YYYY')}
				className="flex-1 border-t border-grey-4 bg-white"
			>
				{loading && sortedAnalytics.length === 0 ? (
					<div className="flex justify-center p-m">
						<Spin />
					</div>
				) : (
					<div className="flex flex-col  gap-s">
						{sortedAnalytics.length === 0 ? (
							<p className="text-xs text-character-secondary">No activity for this month</p>
						) : (
							sortedAnalytics.map((tech) => {
								const totalMs = tech.totalTimeMs || 0;
								return (
									<div key={tech.supplierEmail}>
										<div className="mb-1 flex items-center gap-1">
											<UserNameWithEmail
												showNameWithAvatar
												mode="inline"
												email={tech.supplierEmail}
											/>
										</div>
										<div className="ml-6 flex flex-wrap items-center gap-1">
											<span className="h5medium text-character-primary">
												{formatDuration(totalMs)}
											</span>
											<div className="ml-2 flex items-center gap-2">
												<span className="footnoteregular flex items-center gap-1">
													<ToolOutlined style={{ fontSize: 12 }} />
													{formatDuration(tech.workTimeMs || 0)}
												</span>
												<span className="footnoteregular flex items-center gap-1">
													<CarOutlined style={{ fontSize: 12 }} />
													{formatDuration(tech.driveTimeMs || 0)}
												</span>
											</div>
										</div>
									</div>
								);
							})
						)}
					</div>
				)}
			</Card>
		</div>
	);
};

export default WrenchModeLogsSidebar;
